export type ApplicationCacheHit = "cold" | "module" | "view";

export type ApplicationLifecycleEvent =
  | "app_navigation_started"
  | "app_navigation_module_ready"
  | "remote_registered"
  | "remote_module_load_started"
  | "remote_module_ready"
  | "remote_first_commit"
  | "workspace_first_commit"
  | "application_preload_started"
  | "application_preload_completed"
  | "application_preload_failed"
  | "application_preload_cancelled"
  | "view_evicted";

type ApplicationLifecycleDetail = Record<
  string,
  boolean | number | string | undefined
>;

type Navigation = {
  committed: boolean;
  id: string;
  key: string;
  moduleKey: string | undefined;
  moduleReady: boolean;
  startedAt: number;
  viewKey: string | undefined;
  detail: ApplicationLifecycleDetail;
};

let navigation: Navigation | undefined;
let sequence = 0;
const committedViews = new Set<string>();

export function hasApplicationViewCommit(viewKey: string): boolean {
  return committedViews.has(viewKey);
}

export function forgetApplicationViewCommit(viewKey: string): void {
  committedViews.delete(viewKey);
}

function now(): number {
  return typeof performance === "undefined" ? Date.now() : performance.now();
}

export function applicationNavigationKey(
  scope: string,
  routeKey: string,
): string {
  return `${scope}:${routeKey}`;
}

export function beginApplicationNavigation(
  key: string,
  moduleKey: string | undefined,
  detail: ApplicationLifecycleDetail,
  viewKey?: string,
): string {
  if (navigation?.key === key && navigation.moduleKey === moduleKey) {
    return navigation.id;
  }
  const next: Navigation = {
    committed: false,
    detail,
    id: `navigation-${++sequence}`,
    key,
    moduleKey,
    moduleReady: false,
    startedAt: now(),
    viewKey,
  };
  navigation = next;
  reportApplicationLifecycle("app_navigation_started", {
    ...detail,
    moduleKey,
    navigationId: next.id,
  });
  if (detail.cacheHit === "module" || detail.cacheHit === "view") {
    reportApplicationNavigationModuleReady(moduleKey);
  }
  return next.id;
}

export function reportApplicationNavigationModuleReady(
  moduleKey: string | undefined,
): void {
  if (
    !navigation ||
    !moduleKey ||
    navigation.moduleKey !== moduleKey ||
    navigation.moduleReady
  )
    return;
  navigation.moduleReady = true;
  reportApplicationLifecycle("app_navigation_module_ready", {
    ...navigation.detail,
    durationMs: now() - navigation.startedAt,
    moduleKey,
    navigationId: navigation.id,
  });
}

export function reportApplicationWorkspaceCommit(
  key: string,
  moduleKey?: string,
): void {
  if (
    !navigation ||
    navigation.key !== key ||
    navigation.moduleKey !== moduleKey ||
    navigation.committed
  )
    return;
  navigation.committed = true;
  if (navigation.viewKey) committedViews.add(navigation.viewKey);
  reportApplicationLifecycle(
    moduleKey ? "remote_first_commit" : "workspace_first_commit",
    {
      ...navigation.detail,
      durationMs: now() - navigation.startedAt,
      moduleKey,
      navigationId: navigation.id,
    },
  );
}

export function cancelApplicationNavigation(key: string): void {
  if (navigation?.key === key) navigation = undefined;
}

export function reportApplicationLifecycle(
  event: ApplicationLifecycleEvent,
  detail: ApplicationLifecycleDetail = {},
): void {
  const payload = { ...detail, event, timestamp: Date.now() };
  if (typeof performance !== "undefined" && performance.mark) {
    try {
      performance.mark(`ark:${event}`, { detail: payload });
    } catch {
      performance.mark(`ark:${event}`);
    }
  }
  if (typeof window !== "undefined" && typeof CustomEvent !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("ark:application-lifecycle", { detail: payload }),
    );
  }
}
